import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class PdfDownloadService {
  isGeneratingPDF = false;

  downloadApplicationPDF(getApplicationSummary: () => any, toaster: any): void {
    this.isGeneratingPDF = true;
    try {
      const applicationData = getApplicationSummary();

      const pdfContent = this.generatePDFContent(applicationData);
      this.createAndDownloadPDF(
        pdfContent,
        `Application_${(applicationData.personalInfo?.fullName || 'Application').replace(
          /\s+/g,
          '_'
        )}_${new Date().getTime()}.pdf`
      );

      toaster.success('Application PDF downloaded successfully!', 'Success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toaster.error('Failed to generate PDF. Please try again.', 'Error');
    } finally {
      this.isGeneratingPDF = false;
    }
  }

  private generatePDFContent(applicationData: any): string {
    const currentDate = new Date().toLocaleDateString();

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Application Summary</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
        .header { text-align: center; border-bottom: 2px solid #630f12; padding-bottom: 20px; margin-bottom: 30px; }
        .university-name { color: #630f12; font-size: 24px; font-weight: bold; }
        .section { margin-bottom: 25px; }
        .section-title { background-color: #630f12; color: white; padding: 10px; font-size: 16px; font-weight: bold; }
        .content { padding: 15px; border: 1px solid #ddd; }
        .row { display: flex; margin-bottom: 10px; }
        .label { font-weight: bold; width: 200px; }
        .value { flex: 1; }
        .status { text-align: center; font-size: 18px; font-weight: bold; color: #630f12; margin: 20px 0; }
        .footer { text-align: center; margin-top: 40px; font-size: 12px; color: #666; }
        .logo-container { margin: 10px 0; }
        .logo { 
            max-width: 200px;
            height: auto; 
            display: block;
            margin: 0 auto;
        }
        @media print { body { margin: 0; } }
    </style>
</head>
<body>
    <div class="header">
        <div class="university-name">Techbridge University College, Oyibi</div>
           <div class="logo-container">
             <img src="https://techbridge.edu.gh/static/TUC_LOGO_1.png" alt="AUCDT Logo" class="logo" />
         </div>
        <h2>Student Application Summary</h2>
        <p>Generated on: ${currentDate}</p>
    </div>

    <div class="status">Application Status: ${applicationData.admissionStatus || 'Unknown'}</div>

    <div class="section">
        <div class="section-title">Personal Information</div>
        <div class="content">
            <div class="row"><div class="label">Full Name:</div><div class="value">${
              applicationData.personalInfo?.fullName || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Gender:</div><div class="value">${
              applicationData.personalInfo?.gender || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Date of Birth:</div><div class="value">${
              applicationData.personalInfo?.dateOfBirth || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Birth Place:</div><div class="value">${
              applicationData.personalInfo?.birthPlace || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Nationality:</div><div class="value">${
              applicationData.personalInfo?.nationality || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Religion:</div><div class="value">${
              applicationData.personalInfo?.religion || 'Not specified'
            }</div></div>
            <div class="row"><div class="label">Hometown:</div><div class="value">${
              applicationData.personalInfo?.hometown || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Region:</div><div class="value">${
              applicationData.personalInfo?.region || 'N/A'
            }</div></div>
        </div>
    </div>

    <div class="section">
        <div class="section-title">Contact Information</div>
        <div class="content">
            <div class="row"><div class="label">Postal Address:</div><div class="value">${
              applicationData.contactInfo?.postalAddress || 'N/A'
            }</div></div>
            ${
              applicationData.contactInfo?.postalAddresstwo
                ? `<div class="row"><div class="label"></div><div class="value">${applicationData.contactInfo.postalAddresstwo}</div></div>`
                : ''
            }
            <div class="row"><div class="label">City:</div><div class="value">${
              applicationData.contactInfo?.city || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Country:</div><div class="value">${
              applicationData.contactInfo?.country || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Telephone:</div><div class="value">${
              applicationData.contactInfo?.telephone || 'N/A'
            }</div></div>
            ${
              applicationData.contactInfo?.alternatePhone
                ? `<div class="row"><div class="label">Alternate Phone:</div><div class="value">${applicationData.contactInfo.alternatePhone}</div></div>`
                : ''
            }
            ${
              applicationData.contactInfo?.residentialAddress
                ? `<div class="row"><div class="label">Residential Address:</div><div class="value">${applicationData.contactInfo.residentialAddress}</div></div>`
                : ''
            }
        </div>
    </div>

    <div class="section">
        <div class="section-title">Emergency Contact</div>
        <div class="content">
            <div class="row"><div class="label">Name:</div><div class="value">${
              applicationData.personalInfo?.emergencyContactName || 'Not provided'
            }</div></div>
            <div class="row"><div class="label">Phone:</div><div class="value">${
              applicationData.personalInfo?.emergencyContactPhone || 'Not provided'
            }</div></div>
            <div class="row"><div class="label">Relationship:</div><div class="value">${
              applicationData.personalInfo?.emergencyContactRelationship || 'Not provided'
            }</div></div>
        </div>
    </div>

    <div class="section">
        <div class="section-title">Programme Information</div>
        <div class="content">
            <div class="row"><div class="label">Programme:</div><div class="value">${
              applicationData.programmeInfo?.programme || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Programme Category:</div><div class="value">${
              applicationData.programmeInfo?.programmeType || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Study Mode:</div><div class="value">${
              applicationData.programmeInfo?.preferredStudyMode || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Academic Year:</div><div class="value">${
              applicationData.programmeInfo?.academicYear || 'N/A'
            }</div></div>
        </div>
    </div>

    ${
      applicationData.shsDetails
        ? `
    <div class="section">
        <div class="section-title">SHS/WASSCE Information</div>
        <div class="content">
            <div class="row"><div class="label">Previous School:</div><div class="value">${
              applicationData.shsDetails?.school || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Index Number:</div><div class="value">${
              applicationData.shsDetails?.index || 'N/A'
            }</div></div>
            <div class="row"><div class="label">WASSCE Year:</div><div class="value">${
              applicationData.shsDetails?.wassceYear || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Core Aggregate:</div><div class="value">${
              applicationData.shsDetails?.coreAggregate || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Elective Aggregate:</div><div class="value">${
              applicationData.shsDetails?.electiveAggregate || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Grades:</div><div class="value">${
              applicationData.shsDetails?.grades || 'N/A'
            }</div></div>
        </div>
    </div>
    `
        : ''
    }

    ${
      applicationData.diplomaDetails
        ? `
    <div class="section">
        <div class="section-title">Diploma Information</div>
        <div class="content">
            <div class="row"><div class="label">Institution:</div><div class="value">${
              applicationData.diplomaDetails?.institution || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Program:</div><div class="value">${
              applicationData.diplomaDetails?.program || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Class:</div><div class="value">${
              applicationData.diplomaDetails?.class || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Completion Year:</div><div class="value">${
              applicationData.diplomaDetails?.completionYear || 'N/A'
            }</div></div>
            <div class="row"><div class="label">GPA:</div><div class="value">${
              applicationData.diplomaDetails?.gpa || 'N/A'
            }</div></div>
        </div>
    </div>
    `
        : ''
    }

    ${
      applicationData.matureDetails
        ? `
    <div class="section">
        <div class="section-title">Mature Applicant Information</div>
        <div class="content">
            <div class="row"><div class="label">Exam Status:</div><div class="value">${
              applicationData.matureDetails?.examStatus || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Exam Date:</div><div class="value">${
              applicationData.matureDetails?.examDate || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Years of Experience:</div><div class="value">${
              applicationData.matureDetails?.yearsOfExperience || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Current Employer:</div><div class="value">${
              applicationData.matureDetails?.currentEmployer || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Work Experience:</div><div class="value">${
              applicationData.matureDetails?.experience || 'N/A'
            }</div></div>
        </div>
    </div>
    `
        : ''
    }

    <div class="section">
        <div class="section-title">Financial Information</div>
        <div class="content">
            <div class="row"><div class="label">Payment Mode:</div><div class="value">${
              applicationData.financialInfo?.feePaymentMode || 'N/A'
            }</div></div>
            <div class="row"><div class="label">Scholarship Applied:</div><div class="value">${
              applicationData.financialInfo?.scholarshipApplied ? 'Yes' : 'No'
            }</div></div>
            ${
              applicationData.financialInfo?.scholarshipApplied
                ? `<div class="row"><div class="label">Scholarship Type:</div><div class="value">${applicationData.financialInfo.scholarshipType}</div></div>`
                : ''
            }
        </div>
    </div>

    <div class="section">
        <div class="section-title">Health & Special Needs</div>
        <div class="content">
            <div class="row"><div class="label">Medical Conditions:</div><div class="value">${
              applicationData.healthInfo?.healthCondition === 'Yes' ? applicationData.healthInfo.healthDetails : 'None'
            }</div></div>
            <div class="row"><div class="label">Disabilities:</div><div class="value">${
              applicationData.healthInfo?.disabilities || 'None'
            }</div></div>
            <div class="row"><div class="label">Special Needs:</div><div class="value">${
              applicationData.healthInfo?.specialNeeds || 'None'
            }</div></div>
        </div>
    </div>

    ${
      applicationData.programmeInfo?.otherQualifications ||
      applicationData.programmeInfo?.professionalCertifications ||
      applicationData.programmeInfo?.languageProficiency
        ? `
    <div class="section">
        <div class="section-title">Additional Qualifications</div>
        <div class="content">
            ${
              applicationData.programmeInfo?.otherQualifications
                ? `<div class="row"><div class="label">Other Qualifications:</div><div class="value">${applicationData.programmeInfo.otherQualifications}</div></div>`
                : ''
            }
            ${
              applicationData.programmeInfo?.professionalCertifications
                ? `<div class="row"><div class="label">Professional Certifications:</div><div class="value">${applicationData.programmeInfo.professionalCertifications}</div></div>`
                : ''
            }
            ${
              applicationData.programmeInfo?.languageProficiency
                ? `<div class="row"><div class="label">Language Proficiency:</div><div class="value">${applicationData.programmeInfo.languageProficiency}</div></div>`
                : ''
            }
        </div>
    </div>
    `
        : ''
    }

    <div class="footer">
        <p>This is an official application summary generated by Techbridge UITS Admissions System.</p>
        <p>For inquiries, contact: admissions@techbridge.edu.gh</p>
    </div>
</body>
</html>
    `;
  }

  private createAndDownloadPDF(htmlContent: string, filename: string): void {
    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        printWindow.print();
        // Close the window after printing
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      };
    } else {
      // Fallback: create a blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace('.pdf', '.html');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  }
}
